import { Injectable, Type } from '@angular/core';

import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { InfoDialogComponent, InfoDialogData } from './info-dialog.component';
import { YesNoDialogComponent, YesNoDialogData } from './yes-no-dialog.component';
import { TextPromptDialogComponent, TextPromptDialogData } from './text-prompt-dialog.component';
import { TextboxDialogComponent, TextboxDialogData } from './textbox-dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogWrapperService {
  static ID = 0;

  public localKey: string; // used to distinguish between <p-confirmDialog> on child ( dialog) vs main component.

  // The disign is that there will be a single DailogWrapperService shared by all components
  // within the application, but each dialog will have its own instance with its on 'localKey'
  // that is bound to the 'dialog-key' of its <p-confirmDialog> in the html.
  constructor(
    private confirmationService: ConfirmationService,
    private pngDialogService: DialogService,
  ) {
    if (DialogWrapperService.ID == 0) {
      this.localKey = undefined;
    } else {
      this.localKey = 'dialog-' + DialogWrapperService.ID.toString();
    }
    DialogWrapperService.ID++;
  }

  // used to create dialogs
  async create<TData>(componentType: Type<any>, data?: TData, config?: DynamicDialogConfig<TData>) {
    const ref = this.pngDialogService.open(componentType, {
      closable: false,
      draggable: true,
      modal: true,
      // baseZIndex: 10000,
      // contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      ...config,
      data: data,
    });
    return lastValueFrom(ref.onClose);
  }

  async confirmUndo(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: 'Undo Confirmation',
        icon: 'pi pi-exclamation-triangle',
        message:
          'Are you sure you want to undo changes - all of your work since your last save will be lost.  Do you still want to continue?',
        key: this.localKey,
        accept: () => {
          resolve(true);
        },
        reject: () => {
          resolve(false);
        },
      });
    });
  }

  // key may be needed if multiple p-confirmDialogs are present
  // confirmationService may be needed if dialog refuses to show otherwise (e.g. from app.topbar)
  async okDialog(headerMsg: string, message: string, key?: string, confirmationService?: ConfirmationService): Promise<boolean> {
    const service = confirmationService || this.confirmationService;
    return new Promise((resolve) => {
      service.confirm({
        header: headerMsg,
        icon: 'pi pi-exclamation-triangle',
        message: message,
        acceptLabel: 'Ok',
        rejectVisible: false,
        key: key || this.localKey,
        accept: () => {
          resolve(true);
        },
      });
    });
  }

  // wrapper to enable same signature as old dialogs
  async showOkMessage(title: string, message: string) {
    return this.okDialog(title, message);
  }

  async infoDialog(headerMsg: string, message: string): Promise<boolean> {
    const ref = this.pngDialogService.open(InfoDialogComponent, {
      data: { message } as InfoDialogData,
      header: headerMsg,
      modal: true,
      closable: true,
      draggable: true,
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      width: '500px',
    });
    return lastValueFrom(ref.onClose);
  }

  // Self-contained Yes/No dialog via pngDialogService. Each call opens a fresh
  // dialog instance so it's safe to call repeatedly (including in loops).
  // Result is undefined if the user dismisses via the header X or Esc —
  // normalized to false here so callers can treat the result as a strict boolean.
  async yesNoDialog(headerMsg: string, message: string, yesLabel?: string, noLabel?: string): Promise<boolean> {
    const ref = this.pngDialogService.open(YesNoDialogComponent, {
      data: { message, yesLabel, noLabel } as YesNoDialogData,
      header: headerMsg,
      modal: true,
      closable: true,
      draggable: true,
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      width: '500px',
    });
    const result = await lastValueFrom(ref.onClose);
    return result === true;
  }

  async textboxDialog(headerMsg: string, config: TextboxDialogData): Promise<string> {
    const ref = this.pngDialogService.open(TextboxDialogComponent, {
      data: config,
      header: headerMsg,
      modal: true,
      // style: { 'width': '600px'},
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      // baseZIndex: 10000,
      // maximizable: true
    });
    return lastValueFrom(ref.onClose);
  }

  async textpromptDialog(headerMsg: string, config: TextPromptDialogData): Promise<string> {
    const ref = this.pngDialogService.open(TextPromptDialogComponent, {
      data: config,
      header: headerMsg,
      modal: true,
      contentStyle: { overflow: 'auto' },
      focusOnShow: false,
      width: '500px',
    });
    return lastValueFrom(ref.onClose);
  }
}
